// Pure row-model for the design-system Table. The DS Table renders a flat
// list of primitive-ish row objects; this module maps an OpportunityEdge (a Project with status=OPPORTUNITY,
// as loaded through the ProjectsLoader_* fragments) into that flat shape, and owns the per-column sort
// comparators + the sort <-> sessionStorage translation. It carries the bulk of the feature's logic, so it
// is exhaustively unit-tested and kept free of any React / design-system imports.

import type {SortingFn} from '@tanstack/react-table';
import util from '../shared/util/util';
import {projectUrl, profilePicSrc} from '../../directApi';
import {stageLabel, winBand, type StageBand} from './opportunityStage';
import {deriveOpportunityState, OPPORTUNITY_TAB_MESSAGE_IDS} from './opportunityFilters';
import type {OpportunitySortKey, SortOrder} from './opportunityFilters';
import {targetValueField} from './opportunityInlineEdit';
import type {OpportunityEdge, OpportunityNode, OpportunityPerson} from './opportunityTypes';

// The four mutually-exclusive opportunity states (the filter tabs minus the "all" pseudo-tab).
export type OpportunityState = 'open' | 'won' | 'lost' | 'won_unconverted';

// A minimal intl surface — only formatMessage is needed to resolve the stage / status labels at map time
// (so the columns stay hook-free). Accepts the real react-intl IntlShape or a test stub.
export interface RowIntl {
	formatMessage: (descriptor: {id: string}) => string;
}

export interface OpportunityTableRow {
	id: string;
	customId: string; // "P42" — the colored id badge text
	projectColor: string; // badge background
	name: string;
	clientName: string; // '' when the opportunity has no client
	contactName: string; // '' when there is no primary contact
	contactEmail: string; // '' when the contact has no email
	contactImage?: string; // profile-picture src, undefined -> DS Avatar renders initials
	salespersonName: string; // '' when there is no salesperson
	salespersonImage?: string;
	// Baseline revenue. 0 / missing -> `undefined` (not null): the CurrencyColumn still renders a dash for it,
	// and `undefined` is what lets the column's sortUndefined:'last' pin blank rows to the bottom in BOTH sort
	// directions (a plain comparator can't — the table flips its sign for descending; `null` would not trip
	// that check). Mirrors winChanceSort.
	targetValue: number | undefined;
	currency: string; // ISO code for the CurrencyColumn
	winChance: number; // 0..1, coalesced (DB default 1.00) — drives the band label, dot and stage
	// The value the Win Probability column sorts on: the raw win chance, or `undefined` when it was never
	// set. Kept separate from `winChance` (which coalesces unset to 1.00 for display) so the column can sort
	// unset rows to the bottom in BOTH directions via the DS Table's `sortUndefined: 'last'` — a plain
	// comparator can't, since the table flips its sign for descending. `null` would not trip that check;
	// only `undefined` does. 0% (Lost) stays a real value and sorts normally — it is set, not unset.
	winChanceSort: number | undefined;
	winBandColor: string; // traffic-light dot colour
	stageLabel: string; // localized Opportunity Stage
	statusState: OpportunityState | null; // Open / Won / Lost / Won-Unconverted, or null
	statusLabel: string; // localized status label ('' when stateless)
	startDate: Date | null; // for sorting
	startDateLabel: string | null; // preformatted display ('ll'); null -> "add dates" affordance
	endDate: Date | null;
	endDateLabel: string | null;
	quoteStatus: string | null; // latest quote's status (e.g. 'PUBLISHED'), null when the project has none
	quoteStatusLabel: string; // localized quote status; '' for no quote and for any status not yet mapped
	quoteExpiryDate: Date | null; // the parsed expiry, for sorting
	quoteExpired: boolean; // expiry strictly before today — drives the red expiry treatment (002 D7)
	quoteExpiryLabel: string; // preformatted quote expiry ('ll'); dash when there is no quote or its snapshot carries no expiry
	projectUrlPath: string; // /project/... target for the name-cell navigation
	// Where the name cell navigates. An opportunity is worked in its Baseline — the commercial view — so a
	// project that can actually open one goes there; anything else falls back to the task board
	// (`projectUrlPath`), which the row actions keep using regardless. See 002 D10, including its caveat
	// that useBaseline is a toggle rather than proof a baseline was built out.
	//
	// The verdict comes from Util.isBaselineProject rather than the raw useBaseline flag, because
	// /baseline is guarded three ways and the flag only covers one of them: the route requires
	// VIEW_FINANCIAL_INFORMATION or ..._REVENUE (util.js authorizeAccessRoute 'project-initial-plan'),
	// the BASELINE module must be on, and a RETAINER needs the baseline_on_retainers flag. Linking on
	// useBaseline alone sent users without financial permission to a page that authorises, fails and
	// redirects back to the task board — a wasted round trip and a flicker where they used to land
	// directly — and pointed RETAINER rows at a page their own secondary nav hides.
	opportunityLinkPath: string;
	node: OpportunityNode; // retained for the row actions (add-dates / delete)
}

// Dash shown in the Quote Expiry column when a row has no expiry (matches the DS CurrencyColumn's
// missing-value dash used by Target Value).
export const QUOTE_PLACEHOLDER = '-';

// The expiry is frozen into the quote snapshot as a plain ISO date; parse and render it like the other
// date columns (non-UTC, so the calendar date never shifts with the viewer's timezone).
const quoteExpiry = (expiryDate?: string | null): {label: string; date: Date | null} => {
	if (!expiryDate) return {label: QUOTE_PLACEHOLDER, date: null};
	const [year, month, day] = expiryDate.split('-').map(Number);
	const expiryMoment = util.CreateNonUtcMomentDate(year, month, day);
	return expiryMoment
		? {label: expiryMoment.format('ll'), date: expiryMoment.toDate()}
		: {label: QUOTE_PLACEHOLDER, date: null};
};

// A quote is live through the whole of its expiry date, so "expired" means strictly before today's
// calendar date. Today is built through the same non-UTC path as the expiry itself, so the two are
// comparable and neither drifts with the viewer's timezone.
const isQuoteExpired = (expiry: Date | null): boolean => {
	if (!expiry) return false;
	const now = new Date();
	const today = util.CreateNonUtcMomentDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
	return today ? expiry.getTime() < today.toDate().getTime() : false;
};

/**
 * Every quote status that has a label, and therefore a pill and a sort position (002 D7).
 *
 * A status missing from this map falls through to the no-quote treatment — the same group in the D7
 * ordering, and the same plain text in the cell. A status that becomes reachable later must be added
 * here, or a real quote will read and sort as though the project had none.
 */
export const QUOTE_STATUS_MESSAGE_IDS: Record<string, string> = {
	PUBLISHED: 'opportunities.quote_status.published',
	SENT: 'opportunities.quote_status.sent',
	REVISING: 'opportunities.quote_status.revising',
	APPROVED: 'opportunities.quote_status.approved',
	DECLINED: 'opportunities.quote_status.declined',
};

const quoteStatusLabelOf = (status: string | null, intl: RowIntl): string => {
	const messageId = status ? QUOTE_STATUS_MESSAGE_IDS[status] : undefined;
	return messageId ? intl.formatMessage({id: messageId}) : '';
};

const personName = (p?: OpportunityPerson | null): string => (p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : '');

const personImage = (p?: OpportunityPerson | null): string | undefined =>
	// profilePicSrc returns a URL string for any truthy id (only null for a falsy one, which the guard rules
	// out), so the cast is safe and avoids an unreachable nullish branch.
	p?.profilePictureId ? (profilePicSrc(p.profilePictureId) as string) : undefined;

/**
 * Three sources, one branch: Time & Materials reads the explicitly-entered `baselineTarget`, Fixed
 * Price the agreed `budget`, and Retainer / Non-billable the computed `financialNumbers.baselineRevenue`.
 * The branch itself lives in targetValueField so the column and the inline editor cannot disagree about
 * which field this row's Target Value is in.
 *
 * The `|| undefined` fold is deliberate: 0 and null both render blank rather than "$0", and undefined
 * is what makes a blank sort last in both directions (see TargetValueColumn).
 */
const targetValueOf = (node: OpportunityNode): number | undefined => {
	const field = targetValueField(node);
	const value =
		field === 'baselineTarget'
			? node.baselineTarget
			: field === 'budget'
			? node.budget
			: node.financialNumbers?.baselineRevenue;
	return value || undefined;
};

/**
 * One list row. `bands` is the account's live stage mapping — the labels and colours below are computed
 * against it at render, so a mapping change shows up on the next load with nothing to backfill.
 */
export function toOpportunityTableRow(
	edge: OpportunityEdge,
	intl: RowIntl,
	currency: string,
	bands: StageBand[]
): OpportunityTableRow {
	const node = edge.node;
	const band = winBand(bands, node.baselineWinChance);
	const state = deriveOpportunityState(node);

	const startMoment = util.CreateNonUtcMomentDate(node.projectStartYear, node.projectStartMonth, node.projectStartDay);
	const endMoment = util.CreateNonUtcMomentDate(node.projectEndYear, node.projectEndMonth, node.projectEndDay);
	const projectUrlPath = projectUrl(node.companyProjectId, node.customProjectId);
	const quoteStatus = node.quote?.status ?? null;
	const expiry = quoteExpiry(node.quote?.expiryDate);

	return {
		id: node.id,
		// The project's custom ID when one is set, else the auto "P<companyProjectId>" — matching how the
		// project indicator is shown elsewhere in Forecast (global search, integrations).
		customId: node.customProjectId || `P${node.companyProjectId ?? ''}`,
		projectColor: node.projectColor || '#6b7280',
		name: node.name ?? '',
		clientName: node.client ? node.client.name : '',
		contactName: personName(node.clientContact),
		contactEmail: node.clientContact?.email ?? '',
		contactImage: personImage(node.clientContact),
		salespersonName: personName(node.salesperson),
		salespersonImage: personImage(node.salesperson),
		targetValue: targetValueOf(node),
		currency,
		winChance: node.baselineWinChance ?? 1,
		winChanceSort: node.baselineWinChance ?? undefined, // null/undefined (unset) -> undefined -> sorts last
		winBandColor: band.color,
		stageLabel: stageLabel(bands, node.baselineWinChance, intl),
		statusState: state,
		statusLabel: state ? intl.formatMessage({id: OPPORTUNITY_TAB_MESSAGE_IDS[state]}) : '',
		startDate: startMoment ? startMoment.toDate() : null,
		startDateLabel: startMoment ? startMoment.format('ll') : null,
		endDate: endMoment ? endMoment.toDate() : null,
		endDateLabel: endMoment ? endMoment.format('ll') : null,
		quoteStatus,
		quoteStatusLabel: quoteStatusLabelOf(quoteStatus, intl),
		quoteExpiryLabel: expiry.label,
		quoteExpiryDate: expiry.date,
		quoteExpired: isQuoteExpired(expiry.date),
		projectUrlPath,
		opportunityLinkPath: util.isBaselineProject(null, node) ? `${projectUrlPath}/baseline` : projectUrlPath,
		node,
	};
}

// Case-insensitive string comparison (mirrors the legacy sortOpportunities lowercasing so text columns keep
// ordering the way users expect regardless of capitalisation).
const cmpText = (a: string, b: string): number => {
	const la = a.toLowerCase();
	const lb = b.toLowerCase();
	if (la < lb) return -1;
	if (la > lb) return 1;
	return 0;
};

// Explicit three-way numeric comparison rather than `a - b`. The null-as-lowest columns below map an
// absent value to -Infinity, and `-Infinity - -Infinity` is NaN — a comparator returning NaN leaves the
// sort order unspecified, so two blank rows could swap on every re-render.
const cmpNum = (a: number, b: number): number => (a < b ? -1 : a > b ? 1 : 0);

// 002 D13: a blank is a genuine lowest value, not a permanent floor. Mapping it to -Infinity inside the
// comparator means tanstack's sign-flip for descending does the rest — blanks land first ascending and
// last descending with no sortUndefined and no per-direction special-casing. (Win Probability
// deliberately keeps the opposite behaviour via sortUndefined:'last' on its column: unset there means
// "nobody has judged this deal", which is not the claim "zero" makes.)
const LOWEST = -Infinity;
const numOrLowest = (n: number | undefined | null): number => (n == null ? LOWEST : n);
const timeOrLowest = (d: Date | null): number => (d ? d.getTime() : LOWEST);
const timeOf = (d: Date | null): number => (d ? d.getTime() : 0);

// A comparator over the mapped rows, wrapped into the tanstack SortingFn shape (rowA, rowB) -> number that
// the DS Table expects. The exported type is SortingFn<any> so it satisfies the DS columns' generic
// SortingFnOption<any[]> prop (the DS Table types its data as any[]); the single wrapper keeps every column
// comparator strongly typed against OpportunityTableRow internally.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- match the DS Table's any-typed sortingFn slot
export type OpportunitySortingFn = SortingFn<any>;
type RowComparator = (a: OpportunityTableRow, b: OpportunityTableRow) => number;
const asSortingFn =
	(cmp: RowComparator): OpportunitySortingFn =>
	(a, b) =>
		cmp(a.original, b.original);

// One comparator per sortable column, keyed by the OpportunitySortKey the column advertises.
const byWinChance: RowComparator = (a, b) => a.winChance - b.winChance;
export const opportunitySortingFns: Record<OpportunitySortKey, OpportunitySortingFn> = {
	name: asSortingFn((a, b) => cmpText(a.name, b.name)),
	client: asSortingFn((a, b) => cmpText(a.clientName, b.clientName)),
	contact: asSortingFn((a, b) => cmpText(a.contactName, b.contactName)),
	salesperson: asSortingFn((a, b) => cmpText(a.salespersonName, b.salespersonName)),
	// D13: null is the lowest value, so blanks lead ascending rather than being pinned to the bottom.
	targetValue: asSortingFn((a, b) => cmpNum(numOrLowest(a.targetValue), numOrLowest(b.targetValue))),
	winChance: asSortingFn(byWinChance),
	// D6: Opportunity Stage orders alphabetically by the resolved band label — US2 AC1, which spells the
	// expected order out and places Lost among the open stages ("Contract Sent, Discovery, Lost, New
	// Lead, Proposal Sent, Qualified Opportunity, Won"). It is a text column and sorts like one.
	//
	// It is NOT the band's pipeline position. That was tried and reverted: ordering by startPercentage
	// reads better as a pipeline, but it contradicts AC1, and it is also monotonic in win chance — which
	// reproduces the original complaint, that clicking the Stage header looked like it did nothing.
	// Changing this is a product decision against the story, not a review-thread one.
	stage: asSortingFn((a, b) => cmpText(a.stageLabel, b.stageLabel)),
	startDate: asSortingFn((a, b) => timeOf(a.startDate) - timeOf(b.startDate)),
	endDate: asSortingFn((a, b) => timeOf(a.endDate) - timeOf(b.endDate)),
	status: asSortingFn((a, b) => cmpText(a.statusState ?? '', b.statusState ?? '')),
	// D7: no expiry is the lowest value.
	quoteExpiry: asSortingFn((a, b) => cmpNum(timeOrLowest(a.quoteExpiryDate), timeOrLowest(b.quoteExpiryDate))),
	// D7: two keys — rows with no quote (and any status without a label) form the lowest group, then
	// alphabetically by the localized label the user actually reads.
	quoteStatus: asSortingFn((a, b) => {
		const hasA = a.quoteStatusLabel ? 1 : 0;
		const hasB = b.quoteStatusLabel ? 1 : 0;
		return hasA !== hasB ? hasA - hasB : cmpText(a.quoteStatusLabel, b.quoteStatusLabel);
	}),
};

// The DS Table sorts internally and only accepts a seed (initialSorting) + emits changes (onSortingChange).
// The column ids are the OpportunitySortKeys themselves, so translation to/from the persisted {key, order}
// session shape is a direct 1:1 mapping. Kept pure + tested so the page holds no branchy sort glue.
export interface SessionSort {
	key: OpportunitySortKey;
	order: SortOrder;
}
export type SortingState = Array<{id: string; desc: boolean}>;

export function sessionSortToSortingState(sort: SessionSort | null | undefined): SortingState {
	return sort ? [{id: sort.key, desc: sort.order === 'desc'}] : [];
}

export function sortingStateToSessionSort(state: SortingState): SessionSort | null {
	if (!state.length) return null;
	const {id, desc} = state[0];
	return {key: id as OpportunitySortKey, order: desc ? 'desc' : 'asc'};
}
